import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { EquipmentRegistrationFields, finalizeNumber } from '@/components/registration';
// 添付の保存先は設備用の口（統合台帳なので製造の行でも同じものを使う）
import { engApi } from '@/pages/engineering/engApi';
import { mfgApi, EQUIPMENT_STATUSES } from './mfgApi';
import { useDepartment } from './DepartmentContext';

const EMPTY = {
  acquisition_type: 'existing', name: '', kind: 'machine', category: '', maker: '', maker_pn: '', model_no: '', serial_no: '',
  location_id: '', location: '', department_id: '', status: 'operational', owner: '',
  description: '', reference_url: '', supplier_id: '', supplier_name: '',
  acquired_on: '', acquisition_price: '', source_project_id: '',
  // TECHS 発注に必要な情報（どの台帳でも同じ項目名）
  number_value: null, sku_group_id: '', order_category_id: '', order_category_name: '', uom: '台',
  inspection_interval_months: '', last_inspection_on: '', next_inspection_on: '', inspection_owner: '',
  calibration_required: false, calibration_interval_months: '', last_calibration_on: '',
  next_calibration_on: '', calibration_owner: '',
};

/**
 * F3 · 設備を登録 / 編集（製造 設備・工具）。
 *
 * B2 以降、F2（技術 設備台帳）と**同じ保存先・同じ入力欄**
 * （共通部品 `<EquipmentRegistrationFields>` / 仕様 2.6）。
 * 製造側の項目名（`model` / `serial` / `installed_on` / `notes`）への写しは
 * 保存の直前だけで行う。
 */
export default function EquipmentFormDialog({ open, equipment, onClose, onSaved, fixedDeptCode }) {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  // 2026-10-21 · 登録の種類は form.acquisition_type（共通ブロックが聞く）。
  // 部門は「この画面の部門」を既定にする（製造のページ → 選択中の製造部門）。
  const { departments: ctxDepartments, activeId } = useDepartment();
  const deptId = activeId && activeId !== 'all' ? activeId : '';
  // R1 · 番号の記号は 部署 から決まる（SNS/EQP00100）。場所マスタと同じ ASCII コード。
  const deptCode = (() => {
    if (fixedDeptCode) return fixedDeptCode;      // R2 · 技術（ENG）の見え方から登録
    const id = form.department_id || deptId;
    const row = (departments || ctxDepartments || []).find((d) => d.id === id);
    const code = String(row?.code || '').toUpperCase();
    return code === 'COMMON' ? 'CMN' : code;
  })();

  useEffect(() => {
    if (!open) return;
    mfgApi.employees()
      .then((list) => {
        const rows = Array.isArray(list) ? list : (list?.items || []);
        setEmployees(rows.filter((e) => (e.status || 'active') === 'active')
          .map((e) => ({ id: e.id, name: e.name || e.code || e.email || e.id })));
      })
      .catch(() => setEmployees([]));
    // 2026-10-22 · 部門は部署記号つきのマスターから（技術部 ENG / IT / 共通 CMN も出す）
    mfgApi.equipmentDepartments()
      .then((rows) => setDepartments((rows || []).map((r) => ({
        id: r.department_id || `code:${r.code}`, name: r.name, code: r.code,
      }))))
      .catch(() => setDepartments([]));
    mfgApi.equipment({}).then((rows) => {
      const names = [...new Set((rows || []).map((r) => r.category).filter(Boolean))];
      setCategories(names.map((n) => ({ name: n })));
    }).catch(() => setCategories([]));
    mfgApi.projects?.().then((rows) => setProjects(rows || [])).catch(() => setProjects([]));
  }, [open, ctxDepartments]);

  useEffect(() => {
    if (!open) return;
    if (equipment) {
      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.entries(equipment).filter(([, v]) => v !== null && v !== undefined)),
        model_no: equipment.model || '', serial_no: equipment.serial || '',
        acquired_on: equipment.acquired_on || equipment.installed_on || '',
        description: equipment.notes || '',
        location_id: equipment.location_id || '', location: equipment.location_text || '',
        acquisition_price: equipment.acquisition_price != null ? String(equipment.acquisition_price) : '',
      });
      setFiles(equipment.attachments || []);
    } else {
      setForm({ ...EMPTY, department_id: '' });
      setFiles([]);
    }
  }, [open, equipment, deptId]);

  const save = async () => {
    if (!String(form.name || '').trim()) { toast.error(t('mfg.eq.name')); return; }
    if (!equipment && !deptCode) {
      toast.error('部署を選んでください（番号の部署コードが決まりません）');
      return;
    }
    setBusy(true);
    try {
      const num = (v) => (String(v ?? '').trim() === '' ? null : Number(v));
      const txt = (v) => (String(v ?? '').trim() || null);
      // 番号は保存の瞬間に確定する（ダイアログを閉じただけなら消費しない）。
      let numberFromRule = null;
      if (!equipment) {
        try {
          numberFromRule = await finalizeNumber(form.number_value);
        } catch (e) {
          toast.error(e?.response?.data?.detail || t('mfg.common.error'));
          setBusy(false);
          return;
        }
      }
      const body = {
        asset_no: numberFromRule, sku_template_code: form.number_value?.template_code || null,
        sku_group_id: txt(form.sku_group_id),
        order_category_id: form.order_category_id || null,
        order_category_name: txt(form.order_category_name),
        uom: txt(form.uom),
        name: form.name.trim(), kind: form.kind || 'machine', category: txt(form.category),
        maker: txt(form.maker), maker_pn: txt(form.maker_pn),
        model: txt(form.model_no), serial: txt(form.serial_no),
        location_id: form.location_id || null, location_text: txt(form.location),
        department_id: (form.department_id || '').startsWith('code:')
          ? null : (form.department_id || null),
        dept_code: pickedDept?.code || deptCode || null,
        // 3 · 取得日はそのまま保存する（導入日は別項目）
        acquired_on: form.acquired_on || null,
        installed_on: equipment?.installed_on || form.acquired_on || null,
        status: (!equipment && form.acquisition_type === 'purchase' && form.status === 'operational')
          ? 'on_order' : (form.status || 'operational'),
        owner: txt(form.owner), notes: txt(form.description),
        reference_url: txt(form.reference_url),
        supplier_id: form.supplier_id || null, supplier_name: txt(form.supplier_name),
        acquisition_price: num(form.acquisition_price),
        source_project_id: form.source_project_id || null,
        acquisition_type: form.acquisition_type || 'existing',
        inspection_interval_months: num(form.inspection_interval_months),
        last_inspection_on: form.last_inspection_on || null,
        next_inspection_on: form.next_inspection_on || null,
        inspection_owner: txt(form.inspection_owner),
        calibration_required: !!form.calibration_required,
        calibration_interval_months: num(form.calibration_interval_months),
        last_calibration_on: form.last_calibration_on || null,
        next_calibration_on: form.next_calibration_on || null,
        calibration_owner: txt(form.calibration_owner),
        attachments: files,
      };
      const res = equipment ? await mfgApi.updateEquipment(equipment.id, body)
        : await mfgApi.createEquipment(body);
      toast.success(t('mfg.common.saved'));
      onSaved?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('mfg.common.error'));
    } finally { setBusy(false); }
  };

  // 画面の部署記号（ENG ページなら ENG）に合う部門を既定で選ぶ
  useEffect(() => {
    if (!open || equipment || !departments.length) return;
    setForm((f) => {
      if (f.department_id) return f;
      const want = (fixedDeptCode || '').toUpperCase();
      const row = want ? departments.find((d) => d.code === want)
        : departments.find((d) => d.id === deptId);
      return row ? { ...f, department_id: row.id } : f;
    });
  }, [open, equipment, departments, fixedDeptCode, deptId]);

  const pickedDept = departments.find((d) => d.id === form.department_id);

  const statusOptions = EQUIPMENT_STATUSES
    .filter((s) => s !== 'rented' || equipment?.status === 'rented')
    .filter((s) => s !== 'on_order' || form.acquisition_type === 'purchase'
      || equipment?.status === 'on_order')
    .map((s) => ({ value: s, label: t(`mfg.eq.status.${s}`) }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="equipment-form-dialog">
        <DialogHeader><DialogTitle>{equipment ? t('mfg.eq.edit') : t('mfg.eq.new')}</DialogTitle></DialogHeader>
        <EquipmentRegistrationFields
          askAcquisitionType
          departmentName={pickedDept?.name || ''}
          value={form} onChange={setForm} editing={!!equipment}
          currentNo={equipment?.asset_no || '—'}
          register="equip" appCode="seizo"
          categories={categories} departments={departments} employees={employees}
          projects={projects} statusOptions={statusOptions}
          departmentLabel={t('mfg.common.department')} locationDepartmentId={form.department_id || deptId}
          deptCode={pickedDept?.code || deptCode}
          testPrefix="equipment-form"
          files={files} onFilesChange={setFiles} onUpload={engApi.uploadEquipmentFile}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('mfg.common.cancel')}</Button>
          <Button data-testid="equipment-save-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">
            {busy ? t('mfg.common.saving') : t('mfg.common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
